// @flow
import { useCallback, useState } from 'react';
import type { TypeaheadItem } from '../types';

const trackingDataTypes = {
  LOCATION: 'LOCATION',
  USER: 'USER_PROFILE',
  SEARCH_SUGGESTION: 'QUERY_SUGGESTION',
  SEARCH: 'SEARCH',
};

/**
 * Tracks events using the TypeAheadJson API
 */
const useSearchTracking = (
  searchSessionId: string,
  source: string,
  query: string,
  scopeParams: { [string]: string },
) => {
  /**
   * The startTime gets updated when user click on the typeahead
   */
  const [startTime, setStartTime] = useState('');

  /**
   * Log an impression for the typeahead. This is called when the typeahead is opened.
   */
  const logImpression = useCallback(() => {
    const newStartTime = String(Date.now());

    setStartTime(newStartTime);

    const params = new URLSearchParams({
      action: 'RECORD',
      eventType: 'start',
      uiOrigin: source,
      source,
      startTime: newStartTime,
      hadHistory: 'false',
      searchSessionId,
      query,
      beforeQuery: '',
      afterQuery: query,
      ...scopeParams,
    });

    // $FlowFixMe - navigator.sendBeacon is polyfilled
    navigator.sendBeacon(`/TypeAheadJson?${params.toString()}`);
  }, [source, searchSessionId, query, scopeParams]);

  /**
   * Log when on of the typeahead results is selected
   */
  const logSelection = useCallback(
    (
      interactionType: 'click' | 'enter',
      isRecentLocation: boolean,
      result: TypeaheadItem,
      resultCount: number,
      resultIndex: number,
    ) => {
      const params = new URLSearchParams({
        action: 'RECORD',
        eventType: 'search_from_masthead',
        uiOrigin: source,
        source,
        searchSessionId,
        fromHistory: String(isRecentLocation),
        dataType: trackingDataTypes[result.details.type],
        url: result.url,
        value: result.key,
        indexInType: String(resultIndex),
        selDepth: String(resultIndex + 1), // selDepth starts at 1
        totalNum: String(resultCount),
        startTime,
        userInteractionType: interactionType,
        query,
        documentId: result.key,
        beforeQuery: '',
        afterQuery: result.title,
        ...scopeParams,
      });

      // $FlowFixMe - navigator.sendBeacon is polyfilled
      navigator.sendBeacon(`/TypeAheadJson?${params.toString()}`);
    },
    [source, searchSessionId, query, scopeParams, startTime],
  );

  /**
   * Log when search is executed from typeahead
   */
  const logSearchAction = useCallback(() => {
    const params = new URLSearchParams({
      action: 'RECORD',
      eventType: 'search',
      uiOrigin: source,
      source,
      searchSessionId,
      value: '',
      startTime,
      query,
      beforeQuery: '',
      afterQuery: query,
      ...scopeParams,
    });

    // $FlowFixMe - navigator.sendBeacon is polyfilled
    navigator.sendBeacon(`/TypeAheadJson?${params.toString()}`);
  }, [source, searchSessionId, query, scopeParams, startTime]);

  return {
    logImpression,
    logSelection,
    logSearchAction,
    startTime,
  };
};

export default useSearchTracking;
